import { BodyText, Flex } from '@utilitywarehouse/hearth-react';
import { TickSmallIcon } from '@utilitywarehouse/hearth-react-icons';
import { ReactElement } from 'react';

import { BreakdownItem } from '@components/BreakdownItem';

import { Topics } from './constants';

interface Topic {
  id: Topics;
  title: string;
  details: ReactElement;
}

const DetailsList = (items: string[]): ReactElement => (
  <Flex direction="column" gap="200" asChild>
    <ul className="boilerHomeCoverCovered__BreakdownList">
      {items.map((item, index) => (
        <BreakdownItem key={index}>{item}</BreakdownItem>
      ))}
    </ul>
  </Flex>
);

const BoilerControlsDetails = () => (
  <Flex direction="column" gap="200" asChild>
    <ul className="boilerHomeCoverCovered__BreakdownList">
      <BreakdownItem>
        Repairs to your boiler and controls, the flue, and the gas supply pipe
      </BreakdownItem>
      <li className="boilerHomeCoverCovered__SubListItem">
        <Flex spacing="sm">
          <TickSmallIcon />
          <BodyText>
            If your boiler is Beyond Economic Repair we will do one of the
            following:
          </BodyText>
        </Flex>
        <ul className="boilerHomeCoverCovered__SubList">
          <li>
            <BodyText>
              replace your boiler if it&apos;s less than seven years old
            </BodyText>
          </li>
          <li>
            <BodyText>
              replace your boiler if it is seven to nine years old and was
              bought from us, installed by us, and you&apos;ve had this policy
              continuously since
            </BodyText>
          </li>
          <li>
            <BodyText>
              make a financial contribution (based on how long you have had this
              policy) if it is older than nine years or you don&apos;t meet the
              above criteria
            </BodyText>
          </li>
        </ul>
      </li>
    </ul>
  </Flex>
);

const getTopics = (): ReadonlyArray<Topic> => [
  {
    id: Topics.boilerControls,
    title: 'Boiler and controls',
    details: BoilerControlsDetails(),
  },
  {
    id: Topics.centralHeating,
    title: 'Central heating',
    details: DetailsList([
      'Repairs to your central heating on your property',
      'Replacement of parts of your central heating if we can’t repair them',
      'Accidental damage to your central heating plumbing',
    ]),
  },
  {
    id: Topics.plumbing,
    title: 'Plumbing',
    details: DetailsList([
      'Repairs to the plumbing system on your property',
      "Replacement of parts making up the plumbing system on your property that we can't repair",
      'Accidental damage to the plumbing system on your property',
    ]),
  },
  {
    id: Topics.drains,
    title: 'Drains',
    details: DetailsList([
      'Repairs or unblocking drains to restore flow',
      'Repairs to leaks of internal waste water pipes and external soil and vent pipes',
      "Replacement of parts of your drains that we can't repair",
      'Accidental damage to your drains',
    ]),
  },
  {
    id: Topics.electrics,
    title: 'Electrics',
    details: DetailsList([
      'Repair to the mains electrical system and wiring on your property, including: fuse box, switches, sockets, extractor fans, wired doorbells and wired smoke alarms',
      'Replacement of parts of your mains electrical system and wiring on your property that we can’t repair',
      'Accidental damage to your mains electrical system and the wiring on your property',
    ]),
  },
  {
    id: Topics.pestsSecurity,
    title: 'Pests and Security',
    details: DetailsList([
      'Removal or treatment of pest(s) in your home and wasps or hornet nests on your property',
      'Creating emergency access if you are locked out of your home; boarding up of a broken window or door',
      'Repair or replacement of a broken garage door to make your property secure',
      'Repair of the roof of your home, to stop an emergency which has arisen from damage to the roof',
      'Repair or replacement of locks and keys where the key has snapped in the lock or you have lost the keys',
    ]),
  },
];

export { getTopics };
